import React, {Fragment, useContext, useEffect, useMemo, useState} from "react";
import Button from "../../components/button/button";
import {ButtonStatus} from "../../components/button/enum";
import {Listbox, Transition} from "@headlessui/react";
import useThrottle from "../../util/useThrottle";
import {ProviderContext} from "../providerContext";
import {getCPTCodes, getICDCodes, getTemplateCodes} from "./groupService";
import {mPrint} from "../../util/mPrint";
import Spinner from "../../components/spinner/spinner";
import {PromptContext} from "../promptContext";

export enum ICDType {
    icd9 = 0,
    icd10 = 1,
}

export enum CodeSelectorType {
    icd,
    excludeIcd,
    excludeCpt,
    cpt,
    none,
}

export interface CodeItem {
    code: number | string,
    name: string,
}

export interface CodeTemplateItem {
    id: number | string,
    name: string,
}

interface IProps {
    dataForTemplate: Array<CodeTemplateItem>,
    type: CodeSelectorType,
    selectedCodes: Array<CodeItem>,
    onClose: () => void,
    onConfirm: (selectedCodes: Array<CodeItem>) => void,
}

enum Tab {
    code,
    template,
}

interface ICDCodeTypeItem {
    name: string,
    id: ICDType
}

const dataForIcdCodeType: Array<ICDCodeTypeItem> = [
    {name: "ICD-9", id: ICDType.icd9},
    {name: "ICD-10", id: ICDType.icd10},
]

export default function CodeSelector(props: IProps) {
    const {type, onConfirm, onClose, dataForTemplate} = props
    const [tabIndex, setTabIndex] = useState<Tab>(Tab.code)
    const [dataForCodeSearch, setDataForCodeSearch] = useState<Array<CodeItem>>([])
    const [dataForCodeTemplate, setDataForCodeTemplate] = useState<Array<CodeItem>>([])
    const [selectedCodes, setSelectedCodes] = useState<Array<CodeItem>>(props.selectedCodes)
    const [template, setTemplate] = useState<CodeTemplateItem | null>(null)
    const [keyword, setKeyword] = useState<string>("")
    const provider = useContext(ProviderContext)
    const {showError} = useContext(PromptContext)
    const [icdCodeType, setIcdCodeType] = useState<ICDCodeTypeItem>(dataForIcdCodeType[0])
    const [selectedCodeSet, setSelectedCodeSet] = useState<Set<string | number>>(new Set())
    const [showTips, setShowTips] = useState<boolean>(false)
    const [isShowSpinner, setIsShowSpinner] = useState(false)

    useEffect(() => {
        const set: Set<string | number> = new Set()
        selectedCodes.forEach(({code}) => {
            if (code) {
                set.add(code)
            }
        })
        setSelectedCodeSet(set)
    }, [selectedCodes])

    useEffect(() => {
        if (template) {
            const {id} = template
            setIsShowSpinner(true)
            getTemplateCodes(provider.clinicID, type, id, (list) => {
                setIsShowSpinner(false)
                setDataForCodeTemplate(list)
            }, (error) => {
                setIsShowSpinner(false)
                showError(error)
            })
        }
    }, [template])

    useEffect(() => {
        if (showTips) {
            setTimeout(() => {
                setShowTips(false)
            }, 3000)
        }
    }, [showTips])

    const dataForCode = useMemo(() => {
        if (tabIndex === Tab.code) {
            return dataForCodeSearch
        }
        return dataForCodeTemplate
    }, [tabIndex, dataForCodeSearch, dataForCodeTemplate])

    const getTypeName = () => {
        return type === CodeSelectorType.icd ? "ICD" : "CPT"
    }
    const $title = (
        <p className={"font-semibold text-lg text-gray-1"}>{`Select ${getTypeName()} Code`}</p>
    )

    const $titleView = (
        <div className={"w-full flex flex-row items-center justify-between"}>
            {$title}
        </div>
    )

    const onChangeTabIndex = (idx: Tab) => {
        setTabIndex(idx)
    }
    const $tab = (
        <div className={"w-full flex flex-row items-center"}>
            <button onClick={() => {
                onChangeTabIndex(Tab.code)
            }} className={`flex-1 text-sm font-semibold text-left py-1 border-b ${tabIndex === Tab.code ? "border-primary text-primary hover:text-primary" : "text-gray-3 hover:text-primary"}`}>Search Code</button>
            <button onClick={() => {
                onChangeTabIndex(Tab.template)
            }} className={`flex-1 text-sm font-semibold text-left py-1 border-b ${tabIndex === Tab.template ? "border-primary text-primary hover:text-primary" : "text-gray-3 hover:text-primary"}`}>Add From Template</button>
        </div>
    )

    const $searchIcon = (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.25 15C11.9779 15 15 11.9779 15 8.25C15 4.52208 11.9779 1.5 8.25 1.5C4.52208 1.5 1.5 4.52208 1.5 8.25C1.5 11.9779 4.52208 15 8.25 15Z" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16.5 16.5L13.5 13.5" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )

    const $subTitle = (
        <p className={"text-sm text-gray-1 mt-1 leading-7"}>{`${getTypeName()} Code`}</p>
    )

    const throttleCb = useThrottle((keyword: string) => {
        if (type === CodeSelectorType.icd) {
            setIsShowSpinner(true)
            getICDCodes(icdCodeType.id, keyword, provider.clinicID, (list) => {
                setIsShowSpinner(false)
                setDataForCodeSearch(list)
            }, (error) => {
                setIsShowSpinner(false)
                showError(error)
            })
        } else if (type === CodeSelectorType.cpt) {
            setIsShowSpinner(true)
            getCPTCodes(keyword, provider.clinicID, (list) => {
                setIsShowSpinner(false)
                setDataForCodeSearch(list)
            }, (error) => {
                setIsShowSpinner(false)
                showError(error)
            })
        } else {
            mPrint("Not set...")
        }
    }, 100)

    useEffect(() => {
        if (keyword.length > 1) {
            throttleCb(keyword)
        } else {
            setDataForCodeSearch([])
        }
    }, [keyword])

    const onChangeKeyword = (e: any) => {
        setKeyword(e.target.value)
    }

    const $dropdownForIcdCodeType = (
        <Listbox value={icdCodeType} onChange={(val) => {
            setIcdCodeType(val)
        }}>
            <div className="w-max">
                <Listbox.Button onChange={(val: any) => {
                    setIcdCodeType(val)
                }} type={"button"} className={"flex flex-none flex-row items-center justify-between px-1 py-1 rounded bg-primary space-x-1 hover:bg-blue-700 focus:outline-none"}>
                    <p className={"text-sm text-white truncate"}>{icdCodeType.name}</p>
                    <svg className={"flex-none"} width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.5 2.75L6.01841 4.49079L2.53683 2.75" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </Listbox.Button>
                {dataForIcdCodeType.length ? (
                    <Transition
                        as={Fragment}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <Listbox.Options static className="z-30 bg-white absolute w-max py-1 mt-1.5 overflow-auto text-base bg-white rounded-lg shadow-2xl border focus:outline-none">
                            {dataForIcdCodeType.map((item, idx) => {
                                const title = item.name
                                return (
                                    <Listbox.Option
                                        key={idx}
                                        value={item}
                                        className={"px-1"}
                                    >
                                        {({active }) => {
                                            return (
                                                <div
                                                    className={`${
                                                        active ? "bg-theme-blue-10 text-theme-blue font-semibold" : "bg-white text-gray-1 font-normal"
                                                    } w-full py-2 px-4 text-sm rounded`}
                                                >
                                                    {/* {selected && <i className="fas fa-check mr-4"></i>}*/}
                                                    {title}
                                                </div>
                                            )
                                        }}
                                    </Listbox.Option>
                                )
                            })}
                        </Listbox.Options>
                    </Transition>
                ) : null}
            </div>
        </Listbox>
    )

    const $searchBar = (
        <div className={"w-full"}>
            <div className={"flex flex-row items-center w-100 bg-gray-6 rounded px-2 h-10"}>
                {$searchIcon}
                <input value={keyword} onChange={onChangeKeyword} placeholder={"Search code or keyword here"} className={"flex-1 bg-gray-6 h-full px-2 text-gray-1 text-sm focus:outline-none"}/>
                {type === CodeSelectorType.icd || type === CodeSelectorType.excludeIcd ? $dropdownForIcdCodeType : null}
            </div>
        </div>
    )

    const templateName = template?.name ? template?.name : "Select Template"
    const $dropdownForTemplate = (
        <div className={"relative w-100"}>
            <p className={"text-sm text-gray-1 mt-1 leading-7"}>{`${getTypeName()} Template`}</p>
            <Listbox value={template} onChange={(val) => {
                setTemplate(val)
            }}>
                {({open}) => {
                    return (
                        <div className="w-full">
                            <Listbox.Button className={`w-full px-4 flex flex-row justify-between items-center bg-gray-6 h-10 rounded-md focus:outline-none ${open ? "border border-theme-blue outline-none ring-4 ring-theme-blue-30" : ""}`}>
                                <p className={`text-sm text-left ${templateName.length ? "text-input" : "text-gray-500"}`}>{templateName}</p>
                                <svg width="13" height="6" viewBox="0 0 13 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 2.25L6.51841 3.99079L3.03683 2.25" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Listbox.Button>
                            {dataForTemplate.length ? (
                                <Transition
                                    as={Fragment}
                                    enter="transition duration-100 ease-out"
                                    enterFrom="transform scale-95 opacity-0"
                                    enterTo="transform scale-100 opacity-100"
                                    leave="transition duration-75 ease-out"
                                    leaveFrom="transform scale-100 opacity-100"
                                    leaveTo="transform scale-95 opacity-0"
                                >
                                    <Listbox.Options static className="z-30 bg-white absolute w-full py-1 mt-1.5 overflow-auto text-base bg-white rounded-lg shadow-2xl border max-h-60 focus:outline-none">
                                        {dataForTemplate.map((item, idx) => {
                                            const title = item.name
                                            return (
                                                <Listbox.Option
                                                    key={idx}
                                                    value={item}
                                                    className={"px-1"}
                                                >
                                                    {({active }) => {
                                                        return (
                                                            <div
                                                                className={`${
                                                                    active ? "bg-theme-blue-10 text-theme-blue font-semibold" : "bg-white text-gray-1 font-normal"
                                                                } w-full py-2 px-4 text-sm rounded`}
                                                            >
                                                                {/* {selected && <i className="fas fa-check mr-4"></i>}*/}
                                                                {title}
                                                            </div>
                                                        )
                                                    }}
                                                </Listbox.Option>
                                            )
                                        })}
                                    </Listbox.Options>
                                </Transition>
                            ) : null}
                        </div>
                    )
                }}
            </Listbox>
        </div>
    )

    const onSelectCode = (item: CodeItem) => {
        const existingItem = selectedCodes.find((eItem) => {
            return eItem.code === item.code
        })
        if (!existingItem) {
            setSelectedCodes(selectedCodes.concat([item]))
        }
    }

    const $resultList = (
        <div className={"w-100 mt-2 h-56 rounded border border-border-black-12 overflow-hidden"}>
            <ul className={"w-full h-full overflow-y-scroll py-2"}>
                {dataForCode.map((_item, idx) => {
                    const isSelected = selectedCodeSet.has(_item.code)
                    return <li key={idx} onClick={() => {
                        onSelectCode && onSelectCode(_item)
                    }} className={`cursor-pointer flex flex-row items-center space-x-4 px-2 py-2 text-sm text-gray-1 hover:text-primary bg-white hover:bg-primary-light ${isSelected ? "bg-primary-light font-medium text-primary" : ""}`}>
                        <p className={"w-1/5 break-all flex leading-7 text-sm"}>{_item.code}</p>
                        <p className={"w-4/5 leading-7 text-sm"}>{_item.name}</p>
                    </li>
                })}
            </ul>
        </div>
    )

    const onDeleteSelectedCode = (item: CodeItem) => {
        const data = selectedCodes.filter((_item) => {
            return _item.code !== item.code
        })
        setSelectedCodes(data)
    }

    const $selectedCode = (
        <div className={"mt-16 w-100"}>
            <p className={"w-full text-sm font-medium text-gray-1 leading-7 pt-4"}>Selected Codes</p>
            <div className={"w-full h-56 rounded border border-border-black-12 mt-0 overflow-hidden"}>
                <ul className={"w-full h-full overflow-y-scroll"}>
                    {selectedCodes.map((_item, idx) => {
                        return <li key={idx} className={"cursor-pointer flex flex-row items-center space-x-4 px-2 py-2 text-sm text-gray-1 hover:text-primary hover:font-medium bg-white hover:bg-primary-light"}>
                            <p className={"w-1/5 flex break-all leading-7 text-sm"}>{_item.code}</p>
                            <div className={"w-4/5 flex flex-row items-center justify-between space-x-4"}>
                                <p className={"flex-1 leading-7 text-sm"}>{_item.name}</p>
                                <button onClick={() => {
                                    onDeleteSelectedCode(_item)
                                }} type={"button"} className={"flex flex-none hover:bg-gray-300 p-2 rounded"}>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M11.9963 7.99609L8.00293 11.9894" stroke="#4F4F4F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M11.9975 11.9916L8.00085 7.99414" stroke="#4F4F4F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M13.6121 2.29199H6.38795C3.87045 2.29199 2.29211 4.07449 2.29211 6.59699V13.4037C2.29211 15.9262 3.86295 17.7087 6.38795 17.7087H13.6113C16.1371 17.7087 17.7088 15.9262 17.7088 13.4037V6.59699C17.7088 4.07449 16.1371 2.29199 13.6121 2.29199Z" stroke="#4F4F4F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            </div>
                        </li>
                    })}
                </ul>
            </div>
        </div>
    )

    const $codeSearch = (
        <Fragment>
            {$subTitle}
            {$searchBar}
            {$resultList}
        </Fragment>
    )

    const $templateFilter = (
        <Fragment>
            {$dropdownForTemplate}
            {$resultList}
        </Fragment>
    )

    const $list = tabIndex === Tab.code ? $codeSearch : $templateFilter

    const onAddAllTemplateCodes = () => {
        let map: Map<string | number, CodeItem> = new Map()
        selectedCodes.forEach((_codeItem) => {
            map.set(_codeItem.code, _codeItem)
        })
        let isDuplicated = false
        dataForCode.forEach((_item) => {
            if (!isDuplicated && map.has(_item.code)) {
                isDuplicated = true
            }
            map.set(_item.code, _item)
        })
        let mergedList: Array<CodeItem> = []
        map.forEach((_item) => {
            mergedList.push(_item)
        })

        setShowTips(true)
        setSelectedCodes(mergedList)
    }

    const $addAllCodes = tabIndex === Tab.template && dataForCode.length ? (
        <div className={"w-full flex flex-row justify-end"}>
            <button onClick={() => {
                onAddAllTemplateCodes()
            }} type={"button"} className={"inline-block flex-none py-1 mt-1 font-semibold text-sm leading-tight focus:outline-none focus:ring-0 transition duration-150 ease-in-out text-primary border-b border-transparent hover:border-primary"}>
                +Add all to Selected Codes
            </button>
        </div>
    ) : null

    const $duplicateTips = showTips ? (
        <div className={"mt-2 rounded w-full flex flex-row items-center justify-between px-4 py-1 bg-primary-light text-primary leading-7"}>
            <p className={"text-left text-sm text-gray-1"}>
                Duplicate codes are removed automatically.
            </p>
            <button type={"button"} className={"inline-block"} onClick={() => {
                setShowTips(false)
            }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.5 4.5L14.0806 14.0806" stroke="#2F80ED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4.5 14.0811L14.0806 4.50041" stroke="#2F80ED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>

            </button>
        </div>
    ) : null

    const $contentList = (
        <div className={"flex flex-row space-x-5"}>
            <div className={"flex-1"}>
                {$tab}
                {$list}
                {$addAllCodes}
            </div>
            <div className={"flex-1"}>
                {$selectedCode}
                {$duplicateTips}
            </div>
        </div>
    )

    const $confirmButton = (
        <Button onClick={() => {
            onConfirm && onConfirm(selectedCodes)
        }} >Ok</Button>
    )

    const $cancelButton = (
        <Button status={ButtonStatus.light} onClick={() => {
            onClose && onClose()
        }}>Cancel</Button>
    )

    const $footer = (
        <div className={"w-full flex flex-col items-end mt-2"}>
            <div className={"grid grid-flow-col auto-cols-max gap-x-4 mt-4"}>
                {$cancelButton}
                {$confirmButton}
            </div>
        </div>
    )

    const $spinner = (<Spinner isOpen={isShowSpinner} />)

    return (
        <div className={"bg-white p-4"}>
            {$titleView}
            {$contentList}
            {$footer}
            {$spinner}
        </div>
    )
}
